import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "react-bootstrap";
import moment from "moment";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";
import { Bar } from "react-chartjs-2";

const UserJourney = () => {
  const [journeyData, setJourneyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [forceFilter, setForceFilter] = useState(""); // "", "true", "false"
  const [funnelFilter, setFunnelFilter] = useState("all");
  const [viewMode, setViewMode] = useState("daily"); // "daily" | "hourly"
  const [hourlyDate, setHourlyDate] = useState(moment().format("YYYY-MM-DD"));
  const [hourly, setHourly] = useState(null);
  const [expandedHours, setExpandedHours] = useState({}); // hour -> affiliate rows visible
  const [expandedDays, setExpandedDays] = useState({}); // date -> affiliate rows visible

  // The visualizer events remain collected for analysis, but are intentionally
  // omitted from the journey table. PlansView only fires on /pricing.
  const hiddenStages =
    funnelFilter === "50off-visualizer"
      ? ["VisualizerView", "VisualizerSubmit", "PlansView"]
      : funnelFilter === "50off-pricing"
      // PlansView hidden too: pricing-page entries fire home_view and PlansView
      // on the same page load, so the landing column IS the plans view.
      ? ["VisualizerView", "VisualizerSubmit", "PlansView"]
      : ["VisualizerView", "VisualizerSubmit"];
  const showPlansView = !hiddenStages.includes("PlansView");
  const showVisualizer = !hiddenStages.includes("VisualizerView");
  const landingLabel = funnelFilter === "50off-pricing" ? "Page Viewed" : "Landing Page View";

  // Drops each hidden stage and the label sitting at the same index.
  const dropStages = (stages, labels) => {
    if (!hiddenStages.length) return [stages, labels];
    const keep = stages.map((st) => !hiddenStages.includes(st));
    return [
      stages.filter((_, j) => keep[j]),
      labels ? labels.filter((_, j) => keep[j]) : labels,
    ];
  };

  // Hour-by-hour view for a single day, scoped to the selected funnel.
  const fetchHourly = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axiosClient.get(
        "https://api.viralgpt.com/admin/journey-hourly",
        { params: { date: hourlyDate, funnel: funnelFilter } },
      );
      setHourly(response.data);
    } catch (err) {
      console.error("Error fetching hourly journey:", err);
      setError("Failed to load hourly journey data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch journey data for the date range
  const fetchJourneyData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosClient.get(
        "https://api.viralgpt.com/admin/clickthrough-journey",
        {
          params: {
            startDate,
            endDate,
            forceFilter,
            funnel: funnelFilter,
          },
        },
      );

      // Ensure each day has a dropoffs object
      const processedData = response.data.map((day) => {
        return {
          ...day,
          dropoffs: day.dropoffs || {},
        };
      });

      setJourneyData(processedData);
    } catch (error) {
      console.error("Error fetching user journey data:", error);
      setError("Failed to load user journey data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (viewMode === "hourly") fetchHourly();
    else fetchJourneyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, forceFilter, funnelFilter, viewMode, hourlyDate]);

  // Helper function to check if a date is today
  const isToday = (dateString) => {
    return moment(dateString).isSame(moment(), "day");
  };

  // Format numbers with commas
  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value || 0);
  };

  const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

  // Format percentage values
  const formatPercentage = (value) => {
    return `${value}%`;
  };

  // Helper function to determine retention rate color class
  const getRetentionColorClass = (stage, retentionRate) => {
    const thresholds = {
      VisualizerView: 15,
      PlansView: 15,
      VisualizerSubmit: 10,
      InitiateCheckout: 5,
      CompleteRegistration: 4,
      AddPaymentInfo: 3,
      Purchase: 2,
    };

    const threshold = thresholds[stage];
    if (threshold && retentionRate >= threshold) {
      return "text-success fw-bold";
    } else if (retentionRate > 0) {
      return "text-warning fw-bold";
    } else {
      return "text-danger fw-bold";
    }
  };

  // Calculate totals for the footer
  const calculateTotals = () => {
    if (!journeyData.length) return { dropoffs: {} };

    const totals = journeyData.reduce(
      (acc, day) => {
        return {
          home_view: (acc.home_view || 0) + (parseInt(day.home_view) || 0),
          VisualizerView:
            (acc.VisualizerView || 0) + (parseInt(day.VisualizerView) || 0),
          PlansView:
            (acc.PlansView || 0) + (parseInt(day.PlansView) || 0),
          VisualizerSubmit:
            (acc.VisualizerSubmit || 0) + (parseInt(day.VisualizerSubmit) || 0),
          InitiateCheckout:
            (acc.InitiateCheckout || 0) + (parseInt(day.InitiateCheckout) || 0),
          CompleteRegistration:
            (acc.CompleteRegistration || 0) + (parseInt(day.CompleteRegistration) || 0),
          AddPaymentInfo:
            (acc.AddPaymentInfo || 0) + (parseInt(day.AddPaymentInfo) || 0),
          Purchase: (acc.Purchase || 0) + (parseInt(day.Purchase) || 0),
          revenue: (acc.revenue || 0) + (Number(day.revenue) || 0),
          customers: (acc.customers || 0) + (Number(day.customers) || 0),
          dropoffs: acc.dropoffs || {},
        };
      },
      { dropoffs: {} },
    );

    totals.arpu = totals.customers > 0 ? totals.revenue / totals.customers : 0;

    // Define the funnel stages based on forced/non-forced journey
    let stages;
    if (forceFilter === "true") {
      // Same stages for every traffic filter — the filter narrows who is counted.
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
    } else if (forceFilter === "false") {
      // Same stages for every traffic filter — the filter narrows who is counted.
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
    } else {
      // Landing → visualizer loaded → visualizer submitted → checkout started → payment info → purchase
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
    }

    // Calculate retention rates from home_view for totals
    const homeViews = totals.home_view || 0;
    for (let i = 1; i < stages.length; i++) {
      const currentStage = stages[i];
      const currentCount = totals[currentStage] || 0;

      if (homeViews > 0) {
        const retentionRate = (currentCount / homeViews) * 100;
        totals.dropoffs[`home_view_to_${currentStage}`] = {
          absolute: currentCount,
          percentage: parseFloat(retentionRate.toFixed(2)),
        };
      } else {
        totals.dropoffs[`home_view_to_${currentStage}`] = {
          absolute: 0,
          percentage: 0,
        };
      }
    }

    return totals;
  };

  const totals = calculateTotals();

  // Prepare chart data for funnel visualization
  const prepareFunnelChartData = () => {
    // Define the funnel stages based on forced/non-forced journey
    let stages, labels;

    if (forceFilter === "true") {
      // Same stages for every traffic filter — the filter narrows who is counted.
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      labels = ["Landing Page View", "Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    } else if (forceFilter === "false") {
      // Same stages for every traffic filter — the filter narrows who is counted.
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      labels = ["Landing Page View", "Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    } else {
      // Landing → visualizer loaded → visualizer submitted → checkout started → payment info → purchase
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      labels = ["Landing Page View", "Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    }
    [stages, labels] = dropStages(stages, labels);
    labels[0] = landingLabel;

    return {
      labels: labels,
      datasets: [
        {
          label: "Conversion Funnel",
          data: stages.map((stage) => totals[stage] || 0),
          backgroundColor: [
            "rgba(54, 162, 235, 0.6)",
            "rgba(75, 192, 192, 0.6)",
            "rgba(153, 102, 255, 0.6)",
            "rgba(255, 159, 64, 0.6)",
            "rgba(255, 99, 132, 0.6)",
          ],
          borderColor: [
            "rgba(54, 162, 235, 1)",
            "rgba(75, 192, 192, 1)",
            "rgba(153, 102, 255, 1)",
            "rgba(255, 159, 64, 1)",
            "rgba(255, 99, 132, 1)",
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare chart data for daily conversions
  const prepareDailyConversionData = () => {
    // Get the last 7 days of data
    const last7Days = journeyData
      .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
      .slice(0, 7)
      .reverse();

    // Define the funnel stages based on forced/non-forced journey
    let stages, stageLabels;

    if (forceFilter === "true") {
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      stageLabels = ["Landing Page View", "Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    } else if (forceFilter === "false") {
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      stageLabels = ["Landing Page View", "Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    } else {
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      stageLabels = ["Landing Page View", "Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    }

    [stages, stageLabels] = dropStages(stages, stageLabels);
    stageLabels[0] = landingLabel;

    return {
      labels: last7Days.map((day) => moment(day.date).format("MMM DD")),
      datasets: stages.map((stage, index) => ({
        label: stageLabels[index],
        data: last7Days.map((day) => day[stage] || 0),
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
          "rgba(255, 99, 132, 0.6)",
        ][index % 5],
        borderColor: [
          "rgba(54, 162, 235, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
        ][index % 5],
        borderWidth: 1,
      })),
    };
  };

  // Prepare chart data for dropoff rates
  const prepareDropoffRateData = () => {
    // Define the funnel stages based on forced/non-forced journey
    let stages, retentionLabels;

    if (forceFilter === "true") {
      stages = ["VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      retentionLabels = ["Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    } else if (forceFilter === "false") {
      stages = ["VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      retentionLabels = ["Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    } else {
      stages = ["VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      retentionLabels = ["Visualizer Loaded", "Plans Viewed", "Visualizer Submitted", "Checkout Started", "Account Registered", "Payment Info Entered", "Purchase"];
    }
    [stages, retentionLabels] = dropStages(stages, retentionLabels);

    // Calculate average retention rates from home_view
    const retentionRates = [];
    for (let i = 0; i < stages.length; i++) {
      const currentStage = stages[i];

      // Calculate average retention rate from all days with data
      let totalRate = 0;
      let daysWithData = 0;

      journeyData.forEach((day) => {
        const homeViews = day.home_view || 0;
        const stageCount = day[currentStage] || 0;
        if (homeViews > 0) {
          totalRate += (stageCount / homeViews) * 100;
          daysWithData++;
        }
      });

      const avgRate = daysWithData > 0 ? totalRate / daysWithData : 0;
      retentionRates.push(avgRate);
    }

    return {
      labels: retentionLabels,
      datasets: [
        {
          label: "Average Retention Rate (%)",
          data: retentionRates,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare chart data for conversion rate over time
  const prepareConversionRateTimeData = () => {
    // Get the last 14 days of data
    const last14Days = journeyData
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
      .slice(-14);

    // Define the funnel stages based on forced/non-forced journey
    let stages;

    if (forceFilter === "true") {
      stages = ["home_view", "Purchase"];
    } else if (forceFilter === "false") {
      stages = ["home_view", "Purchase"];
    } else {
      stages = ["home_view", "Purchase"];
    }

    // Calculate daily conversion rates
    const conversionRates = last14Days.map((day) => {
      const firstStage = day[stages[0]] || 0;
      const lastStage = day[stages[1]] || 0;
      return firstStage > 0 ? (lastStage / firstStage) * 100 : 0;
    });

    return {
      labels: last14Days.map((day) => moment(day.date).format("MMM DD")),
      datasets: [
        {
          label: "Overall Conversion Rate (%)",
          data: conversionRates,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
          fill: false,
          tension: 0.1,
        },
      ],
    };
  };

  // Chart options
  const funnelChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Conversion Funnel Totals",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Views",
        },
      },
    },
  };

  const dailyConversionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Daily Conversions (Last 7 Days)",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Views",
        },
      },
    },
  };

  const dropoffRateOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Average Retention Rates (from Landing Page Views)",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Retention Rate (%)",
        },
      },
    },
  };

  const conversionRateTimeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Overall Conversion Rate Over Time",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Conversion Rate (%)",
        },
      },
    },
  };

  const renderHourlyTable = () => {
    if (!hourly) return null;
    const cell = { fontSize: "0.85rem" };
    return (
      <Table striped bordered hover size="sm">
        <thead className="bg-light">
          <tr>
            <th style={{ width: 34 }}></th>
            <th>Hour (CDT)</th>
            <th>{landingLabel}</th>
            {showVisualizer && (<><th>VL %</th><th>Visualizer Loaded</th></>)}
            {showPlansView && (<><th>PV %</th><th>Plans Viewed</th></>)}
            {showVisualizer && (<><th>VS %</th><th>Visualizer Submitted</th></>)}
            <th>CS %</th>
            <th>Checkout Started</th>
            <th>AR %</th>
            <th>Account Registered</th>
            <th>PI %</th>
            <th>Payment Info Entered</th>
            <th>CVR %</th>
            <th>Purchase</th>
          </tr>
        </thead>
        <tbody>
          {hourly.hours.map((h) => (
            <React.Fragment key={h.hour}>
              <tr style={h.home_view === 0 ? { opacity: 0.45 } : undefined}>
                <td style={{ ...cell, textAlign: "center", padding: 0 }}>
                  {(h.affiliates || []).length > 0 && (
                    <Button
                      variant="link"
                      size="sm"
                      style={{ padding: "0 6px", textDecoration: "none", lineHeight: 1 }}
                      onClick={() => setExpandedHours((prev) => ({ ...prev, [h.hour]: !prev[h.hour] }))}
                    >
                      {expandedHours[h.hour] ? "▾" : "▸"}
                    </Button>
                  )}
                </td>
                <td style={cell}>{((h.hour % 12) || 12)}:00 {h.hour < 12 ? "AM" : "PM"}</td>
                <td style={cell}>{h.home_view}</td>
                {showVisualizer && (<><td style={cell}>{h.vlRate}%</td><td style={cell}>{h.VisualizerView}</td></>)}
                {showPlansView && (<><td style={cell}>{h.pvRate}%</td><td style={cell}>{h.PlansView}</td></>)}
                {showVisualizer && (<><td style={cell}>{h.vsRate}%</td><td style={cell}>{h.VisualizerSubmit}</td></>)}
                <td style={cell}>{h.icRate}%</td>
                <td style={cell}>{h.InitiateCheckout}</td>
                <td style={cell}>{h.arRate}%</td>
                <td style={cell}>{h.CompleteRegistration}</td>
                <td style={cell}>{h.piRate}%</td>
                <td style={cell}>{h.AddPaymentInfo}</td>
                <td style={{ ...cell, fontWeight: 700, background: h.cvr >= 1.5 ? "#d4f4e2" : h.cvr > 0 ? "#fff8e1" : undefined }}>
                  {h.cvr}%
                </td>
                <td style={{ ...cell, fontWeight: 700 }}>{h.Purchase}</td>
              </tr>
              {expandedHours[h.hour] &&
                (h.affiliates || []).map((a) => (
                  <tr key={`${h.hour}-${a.affiliate}`} style={{ background: "#f6f8fa" }}>
                    <td></td>
                    <td style={{ ...cell, paddingLeft: 18, color: "#6c757d", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.affiliate}>
                      {a.affiliate}
                    </td>
                    <td style={cell}>{a.home_view}</td>
                    {showVisualizer && (<><td style={cell}>{a.vlRate}%</td><td style={cell}>{a.VisualizerView}</td></>)}
                    {showPlansView && (<><td style={cell}>{a.pvRate}%</td><td style={cell}>{a.PlansView}</td></>)}
                    {showVisualizer && (<><td style={cell}>{a.vsRate}%</td><td style={cell}>{a.VisualizerSubmit}</td></>)}
                    <td style={cell}>{a.icRate}%</td>
                    <td style={cell}>{a.InitiateCheckout}</td>
                    <td style={cell}>{a.arRate}%</td>
                    <td style={cell}>{a.CompleteRegistration}</td>
                    <td style={cell}>{a.piRate}%</td>
                    <td style={cell}>{a.AddPaymentInfo}</td>
                    <td style={cell}>{a.cvr}%</td>
                    <td style={{ ...cell, fontWeight: 700 }}>{a.Purchase}</td>
                  </tr>
                ))}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot className="table-dark">
          <tr>
            <td style={{ textAlign: "center", padding: 0 }}>
              {(hourly.affiliateTotals || []).length > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  style={{ padding: "0 6px", textDecoration: "none", lineHeight: 1, color: "#fff" }}
                  onClick={() => setExpandedHours((prev) => ({ ...prev, total: !prev.total }))}
                >
                  {expandedHours.total ? "▾" : "▸"}
                </Button>
              )}
            </td>
            <td><strong>TOTAL</strong></td>
            <td><strong>{hourly.totals.home_view}</strong></td>
            {showVisualizer && (<><td><strong>{hourly.totals.vlRate}%</strong></td><td><strong>{hourly.totals.VisualizerView}</strong></td></>)}
            {showPlansView && (<><td><strong>{hourly.totals.pvRate}%</strong></td><td><strong>{hourly.totals.PlansView}</strong></td></>)}
            {showVisualizer && (<><td><strong>{hourly.totals.vsRate}%</strong></td><td><strong>{hourly.totals.VisualizerSubmit}</strong></td></>)}
            <td><strong>{hourly.totals.icRate}%</strong></td>
            <td><strong>{hourly.totals.InitiateCheckout}</strong></td>
            <td><strong>{hourly.totals.arRate}%</strong></td>
            <td><strong>{hourly.totals.CompleteRegistration}</strong></td>
            <td><strong>{hourly.totals.piRate}%</strong></td>
            <td><strong>{hourly.totals.AddPaymentInfo}</strong></td>
            <td><strong>{hourly.totals.cvr}%</strong></td>
            <td><strong>{hourly.totals.Purchase}</strong></td>
          </tr>
          {expandedHours.total &&
            (hourly.affiliateTotals || []).map((a) => {
              const subCell = { ...cell, background: "#f6f8fa", color: "#212529" };
              return (
                <tr key={`total-${a.affiliate}`}>
                  <td style={subCell}></td>
                  <td style={{ ...subCell, paddingLeft: 18, color: "#6c757d", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.affiliate}>
                    {a.affiliate}
                  </td>
                  <td style={subCell}>{a.home_view}</td>
                  {showVisualizer && (<><td style={subCell}>{a.vlRate}%</td><td style={subCell}>{a.VisualizerView}</td></>)}
                  {showPlansView && (<><td style={subCell}>{a.pvRate}%</td><td style={subCell}>{a.PlansView}</td></>)}
                  {showVisualizer && (<><td style={subCell}>{a.vsRate}%</td><td style={subCell}>{a.VisualizerSubmit}</td></>)}
                  <td style={subCell}>{a.icRate}%</td>
                  <td style={subCell}>{a.InitiateCheckout}</td>
                  <td style={subCell}>{a.arRate}%</td>
                  <td style={subCell}>{a.CompleteRegistration}</td>
                  <td style={subCell}>{a.piRate}%</td>
                  <td style={subCell}>{a.AddPaymentInfo}</td>
                  <td style={subCell}>{a.cvr}%</td>
                  <td style={{ ...subCell, fontWeight: 700 }}>{a.Purchase}</td>
                </tr>
              );
            })}
        </tfoot>
      </Table>
    );
  };

  // Render the appropriate table based on the filter
  const renderJourneyTable = () => {
    // Define the funnel stages and headers based on forced/non-forced journey
    let stages, headers;

    if (forceFilter === "true") {
      // Same stages for every traffic filter — the filter narrows who is counted.
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      headers = [
        "Date",
        "Landing Page View",
        "VL %",
        "Visualizer Loaded",
        "PV %",
        "Plans Viewed",
        "VS %",
        "Visualizer Submitted",
        "CS %",
        "Checkout Started",
        "AR %",
        "Account Registered",
        "PI %",
        "Payment Info Entered",
        "CVR %",
        "Purchase",
      ];
    } else if (forceFilter === "false") {
      // Same stages for every traffic filter — the filter narrows who is counted.
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      headers = [
        "Date",
        "Landing Page View",
        "VL %",
        "Visualizer Loaded",
        "PV %",
        "Plans Viewed",
        "VS %",
        "Visualizer Submitted",
        "CS %",
        "Checkout Started",
        "AR %",
        "Account Registered",
        "PI %",
        "Payment Info Entered",
        "CVR %",
        "Purchase",
      ];
    } else {
      // Landing → visualizer loaded → visualizer submitted → checkout started → payment info → purchase
      stages = ["home_view", "VisualizerView", "PlansView", "VisualizerSubmit", "InitiateCheckout", "CompleteRegistration", "AddPaymentInfo", "Purchase"];
      headers = [
        "Date",
        "Landing Page View",
        "VL %",
        "Visualizer Loaded",
        "PV %",
        "Plans Viewed",
        "VS %",
        "Visualizer Submitted",
        "CS %",
        "Checkout Started",
        "AR %",
        "Account Registered",
        "PI %",
        "Payment Info Entered",
        "CVR %",
        "Purchase",
      ];
    }

    // headers isn't parallel to stages — Date, Landing Page View, then a rate
    // and a count per remaining stage — so drop the matching pair by index.
    // Highest index first, so the earlier ones stay valid as we splice.
    if (hiddenStages.length) {
      const drop = stages
        .map((st, i) => (hiddenStages.includes(st) ? i : -1))
        .filter((i) => i > 0)
        .sort((a, b) => b - a);
      drop.forEach((i) => {
        const h = 2 + (i - 1) * 2;
        stages = stages.filter((_, j) => j !== i);
        headers = headers.filter((_, j) => j !== h && j !== h + 1);
      });
    }
    headers[1] = landingLabel;
    headers.push("ARPU");

    return (
      <Table striped bordered hover className="journey-table">
        <thead className="bg-light">
          <tr>
            <th style={{ width: 34 }}></th>
            {headers.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {journeyData
            .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()) // Sort by date descending
            .map((day) => {
              const homeViews = day.home_view || 0;
              
              const subCell = { fontSize: "0.85rem", background: "#f6f8fa", color: "#212529" };
              return (
                <React.Fragment key={day.date}>
                  <tr className={isToday(day.date) ? "today-highlight" : ""}>
                    <td style={{ textAlign: "center", padding: 0 }}>
                      {(day.affiliates || []).length > 0 && (
                        <Button
                          variant="link"
                          size="sm"
                          style={{ padding: "0 6px", textDecoration: "none", lineHeight: 1 }}
                          onClick={() => setExpandedDays((prev) => ({ ...prev, [day.date]: !prev[day.date] }))}
                        >
                          {expandedDays[day.date] ? "▾" : "▸"}
                        </Button>
                      )}
                    </td>
                    <td>
                      {isToday(day.date) ? (
                        <span className="today-label">
                          {moment(day.date).format("MMM DD, YYYY")}
                        </span>
                      ) : (
                        moment(day.date).format("MMM DD, YYYY")
                      )}
                    </td>

                    {/* Landing Page View */}
                    <td>{formatNumber(homeViews)}</td>

                    {/* Rest of the stages with retention % from home_view */}
                    {stages.slice(1).map((stage) => {
                      const stageCount = day[stage] || 0;
                      const retentionRate = homeViews > 0 ? (stageCount / homeViews) * 100 : 0;

                      return (
                        <React.Fragment key={stage}>
                          <td className={getRetentionColorClass(stage, retentionRate)}>
                            {formatPercentage(retentionRate.toFixed(2))}
                          </td>
                          <td>{formatNumber(stageCount)}</td>
                        </React.Fragment>
                      );
                    })}
                    <td>{formatCurrency(day.arpu)}</td>
                  </tr>
                  {expandedDays[day.date] &&
                    (day.affiliates || []).map((a) => (
                      <tr key={`${day.date}-${a.affiliate}`}>
                        <td style={subCell}></td>
                        <td style={{ ...subCell, paddingLeft: 18, color: "#6c757d", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.affiliate}>
                          {a.affiliate}
                        </td>
                        <td style={subCell}>{formatNumber(a.home_view || 0)}</td>
                        {stages.slice(1).map((stage) => {
                          const stageCount = a[stage] || 0;
                          const affViews = a.home_view || 0;
                          const retentionRate = affViews > 0 ? (stageCount / affViews) * 100 : 0;
                          return (
                            <React.Fragment key={stage}>
                              <td style={subCell}>{formatPercentage(retentionRate.toFixed(2))}</td>
                              <td style={subCell}>{formatNumber(stageCount)}</td>
                            </React.Fragment>
                          );
                        })}
                        <td style={subCell}>{formatCurrency(a.arpu)}</td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
        </tbody>
        <tfoot className="table-dark">
          <tr>
            <td></td>
            <td>
              <strong>TOTALS</strong>
            </td>

            {/* Landing Page View */}
            <td>{formatNumber(totals.home_view || 0)}</td>

            {/* Render cells for each stage with retention % from home_view */}
            {stages.slice(1).map((stage) => {
              const stageCount = totals[stage] || 0;
              const homeViews = totals.home_view || 0;
              const retentionRate = homeViews > 0 ? (stageCount / homeViews) * 100 : 0;
              
              return (
                <React.Fragment key={stage}>
                  <td className={getRetentionColorClass(stage, retentionRate)}>
                    {formatPercentage(retentionRate.toFixed(2))}
                  </td>
                  <td>{formatNumber(stageCount)}</td>
                </React.Fragment>
              );
            })}
            <td><strong>{formatCurrency(totals.arpu)}</strong></td>
          </tr>
        </tfoot>
      </Table>
    );
  };

  return (
    <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)',marginBottom:'1.5rem'}}>
      <div style={{padding:0}}>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 p-3">
          <span style={{fontWeight:700,color:'#0a2540',fontSize:'1.1rem'}}>User Clickthrough Journey</span>
          <div className="d-flex gap-2 mt-2 mt-sm-0 w-100 w-sm-auto">
            <Form.Group as={Row} className="align-items-center w-100">
              <Form.Label column sm="auto">
                Date Range:
              </Form.Label>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col sm="auto">to</Col>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              <Col sm="auto">
                <Form.Select
                  value={forceFilter}
                  onChange={(e) => setForceFilter(e.target.value)}
                >
                  <option value="">All Users</option>
                  <option value="true">Has FBCLID</option>
                  <option value="false">No FBCLID</option>
                </Form.Select>
              </Col>
              <Col sm="auto">
                <Form.Select
                  value={funnelFilter}
                  onChange={(e) => setFunnelFilter(e.target.value)}
                  title="Filter by funnel"
                >
                  <option value="all">All funnels</option>
                  <option value="50off">50off funnel (all)</option>
                  <option value="50off-pricing">50off (pricing page)</option>
                  <option value="50off-visualizer">50off (visualizer)</option>
                  <option value="main">Main funnel</option>
                  <option value="untagged">Untagged (pre-tracking)</option>
                </Form.Select>
              </Col>
              <Col sm="auto">
                <Form.Select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  title="Daily range or hour-by-hour"
                >
                  <option value="daily">Daily</option>
                  <option value="hourly">Hour by hour</option>
                </Form.Select>
              </Col>
              {viewMode === "hourly" && (
                <Col sm="auto">
                  <Form.Control
                    type="date"
                    value={hourlyDate}
                    onChange={(e) => setHourlyDate(e.target.value)}
                    title="Day to break down"
                  />
                </Col>
              )}
              <Col sm="auto">
                <button style={{background:'linear-gradient(135deg,#635bff 0%,#7c3aed 100%)',color:'#fff',border:'none',borderRadius:'10px',fontWeight:600,fontSize:'0.85rem',padding:'0.5rem 1rem',cursor:'pointer',boxShadow:'0 4px 6px rgba(50,50,93,0.11), 0 1px 3px rgba(0,0,0,0.08)'}} onClick={() => (viewMode === 'hourly' ? fetchHourly() : fetchJourneyData())}>
                  Refresh
                </button>
              </Col>
            </Form.Group>
          </div>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              {viewMode === "hourly" ? renderHourlyTable() : renderJourneyTable()}
            </div>

            {viewMode === "hourly" && hourly?.availableFunnels?.length > 0 && (
              <div className="px-3 pb-2" style={{ fontSize: "0.8rem", color: "#8898aa" }}>
                Events on this day by funnel:{" "}
                {hourly.availableFunnels.map((f) => `${f.funnel} (${f.count})`).join(" · ")}
              </div>
            )}

            {viewMode !== "hourly" && (
            <>
            {/* Charts Section */}
            <div className="p-3">
              <h4 style={{fontWeight:700,color:'#0a2540',fontSize:'1.1rem'}} className="mb-4">Journey Analytics</h4>

              <Row className="mb-4">
                <Col md={6}>
                  <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
                    <div style={{padding:'1.5rem'}}>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareFunnelChartData()}
                          options={funnelChartOptions}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
                    <div style={{padding:'1.5rem'}}>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareDropoffRateData()}
                          options={dropoffRateOptions}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col md={6}>
                  <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
                    <div style={{padding:'1.5rem'}}>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareDailyConversionData()}
                          options={dailyConversionOptions}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div style={{background:'#fff',borderRadius:'16px',border:'1px solid #e3e8ee',boxShadow:'0 2px 4px rgba(50,50,93,0.06)'}}>
                    <div style={{padding:'1.5rem'}}>
                      <div style={{ height: "400px" }}>
                        <Bar
                          data={prepareConversionRateTimeData()}
                          options={conversionRateTimeOptions}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            </>
            )}

            <style jsx="true">{`
              .today-label {
                font-weight: bold;
                color: #daa520;
              }
              .today-highlight {
                background-color: rgba(218, 165, 32, 0.1);
              }

              /* Default (non-mobile) table styling */
              .journey-table {
                font-size: 0.9rem;
              }

              /* Mobile adjustments */
              @media (max-width: 576px) {
                .journey-table {
                  table-layout: fixed;
                  width: 100%;
                  font-size: 0.4rem;
                }
                .journey-table th,
                .journey-table td {
                  padding: 0;
                  text-align: center;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: clip;
                }
              }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
};

export default UserJourney;
